import { track } from "@amplitude/analytics-react-native";
import { setStringAsync } from "expo-clipboard";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { amp } from "src/api/amplitude";
import haptic from "src/helpers/haptic";
import log from "src/helpers/log";
import { enumNever } from "src/shared/helpers/generalHelpers";
import { Timeout } from "src/types/time";
import { ShareContextType, useShareContext } from "../../contexts/ShareContext";
import copyImageToClipboard from "../../helpers/copyImageToClipboard";
import getUniqueTypes from "../../helpers/getUniqueTypes";
import { ShareClipboardDataType } from "./shareClipboardHelpers";

const useShareClipboard = (data: ShareClipboardDataType, context?: ShareContextType) => {
	const { prefetched, onComplete, onCopy, onCopyClear, onSuccess } = useShareContext(context);
	const [loading, setLoading] = useState(false);
	const [copied, setCopied] = useState(false);

	const prevCopied = useRef<boolean | undefined>(copied);
	// biome-ignore lint/correctness/useExhaustiveDependencies: Should only run if copied changes
	useEffect(() => {
		if (prevCopied.current != null && !copied) {
			onCopyClear?.();
		}
		if (prevCopied.current != null && copied) {
			haptic("Success");
			onCopy?.();
			onSuccess?.();
		}
		prevCopied.current = copied;
	}, [copied]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Should run when data changes
	useEffect(() => {
		setCopied(false);
	}, [data]);

	const timeout = useRef<Timeout | null>(null);
	const handleConfirmCopy = useCallback(() => {
		setCopied(true);
		timeout.current = setTimeout(() => {
			onComplete?.();
		}, 600);
	}, [onComplete]);
	useEffect(() => {
		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	const handlePress = useCallback(async () => {
		try {
			setLoading(true);
			track(amp.share.clipboard, {
				types: getUniqueTypes(data),
			});
			const item = data[0];
			if (!item) {
				throw new Error("Missing item");
			}
			const type = item.type;
			switch (type) {
				case "Text":
				case "Url":
					const body = type === "Text" ? item.text : item.url;
					await setStringAsync(body);
					break;
				case "Image":
				case "LivePhoto":
					const prefetchedData = prefetched.current[item.url];
					if (!prefetchedData) {
						throw new Error("Missing expected prefetched result");
					}
					await copyImageToClipboard(prefetchedData);
					break;
				default:
					return enumNever(type);
			}
			handleConfirmCopy();
			if (item.successAmplitude) {
				track(item.successAmplitude.eventName, {
					...item.successAmplitude.eventProperties,
					shareType: item.type,
					shareAction: "Clipboard",
				});
			}
		} catch (err) {
			log.error({ err }, "Error copying");
		} finally {
			setLoading(false);
		}
	}, [data, handleConfirmCopy, prefetched]);

	const copyText = useMemo(() => {
		const item = data[0];
		const type = item?.type ?? "Text";
		switch (type) {
			case "Url":
				return "Copy Link";
			case "Image":
			case "LivePhoto":
			case "Text":
				return "Copy";
			default:
				return enumNever(type);
		}
	}, [data]);

	return {
		loading,
		handlePress,
		copied,
		copyText,
	};
};
export default useShareClipboard;
